/**
 * À la carte catalog — LOCKED from V2 §21 (mem://features/a-la-carte).
 *
 * 14 features total. Incognito is FREE on every tier and is rendered as a
 * safety note, not a purchasable card. RESONATE is MindLynq-only (UI hint
 * here; the real gate is server-side in create-alacarte-checkout).
 *
 * The frontend uses this for display only. Actual prices live in the
 * `alacarte_prices` table on the server and are resolved by the edge
 * function via (feature_key, purchase_type).
 */

import { supabase } from "@/integrations/supabase/client";
import type { AppId } from "@/lib/apps";

export type AlacarteFeatureKey =
  | "message_reading"
  | "roam_mode"
  | "amplify_signal"
  | "bgc_plus"
  | "event_pass"
  | "priority_matching"
  | "advanced_analytics"
  | "replay_pack"
  | "resonate_pack"
  | "voice_wave_plus"
  | "community_verified"
  | "event_host_pass"
  | "profile_spotlight";

export type PurchaseType = "monthly" | "one_time";

export interface AlacartePriceOption {
  type: PurchaseType;
  /** USD display price (cents). Server resolves the real price. */
  amountCents: number;
  /** Short label under the price, e.g. "/mo", "24h", "25 reads". */
  unit: string;
}

export interface AlacarteItem {
  key: AlacarteFeatureKey;
  name: string;
  /** Short pitch — under 80 chars. */
  tagline: string;
  /** Full description shown on the card body. */
  description: string;
  /** Available purchase variants. Order is what we render in the UI. */
  options: AlacartePriceOption[];
  /** App scope. undefined = available everywhere. */
  appScope?: AppId;
  /** True for features that require an event_id to purchase. */
  requiresEvent?: boolean;
  /** Category for grouping in the UI. */
  category: "communication" | "discovery" | "verification" | "events" | "profile";
}

export const ALACARTE_ITEMS: AlacarteItem[] = [
  {
    key: "message_reading",
    name: "Message Reading",
    tagline: "Read receipts on the messages you send.",
    description:
      "See when your messages are opened, with a timestamp. Sender-side only — your read state is never exposed without your own purchase.",
    options: [
      { type: "monthly", amountCents: 299, unit: "/mo" },
      { type: "one_time", amountCents: 99, unit: "25 reads" },
    ],
    category: "communication",
  },
  {
    key: "roam_mode",
    name: "ROAM Mode",
    tagline: "Discover beyond your distance radius.",
    description:
      "Removes the geographic radius filter so you can match nationally. A ROAMING badge shows on your profile while active.",
    options: [
      { type: "monthly", amountCents: 299, unit: "/mo" },
      { type: "one_time", amountCents: 99, unit: "7 days" },
    ],
    category: "discovery",
  },
  {
    key: "amplify_signal",
    name: "AMPLIFY Signal",
    tagline: "Top of the discovery feed.",
    description:
      "Boost your profile to the top of the discovery feed for compatible members. Discovery sort: AMPLIFY → Priority Matching → standard.",
    options: [
      { type: "monthly", amountCents: 499, unit: "/mo" },
      { type: "one_time", amountCents: 199, unit: "24h" },
    ],
    category: "discovery",
  },
  {
    key: "bgc_plus",
    name: "Background Check Plus",
    tagline: "Optional verification badge — permanent on pass.",
    description:
      "Run an optional background check. On pass, the Background Checked ✓ badge stays on your profile permanently — even if you cancel the subscription.",
    options: [
      { type: "monthly", amountCents: 599, unit: "/mo" },
      { type: "one_time", amountCents: 199, unit: "single check" },
    ],
    category: "verification",
  },
  {
    key: "event_pass",
    name: "Event Pass",
    tagline: "RSVP to Wavlynq-hosted events on Signal.",
    description:
      "Required on the free Signal tier to RSVP to Wavlynq-hosted events. Pulse and above already include RSVPs. Single-purchase locks to one event.",
    options: [
      { type: "monthly", amountCents: 999, unit: "/mo" },
      { type: "one_time", amountCents: 599, unit: "single event" },
    ],
    requiresEvent: true,
    category: "events",
  },
  {
    key: "priority_matching",
    name: "Priority Matching",
    tagline: "Surface to the top of compatible queues.",
    description:
      "Get pushed to the top of compatible members' queues. Stacks with AMPLIFY — both can be active at the same time.",
    options: [
      { type: "monthly", amountCents: 399, unit: "/mo" },
      { type: "one_time", amountCents: 99, unit: "2h" },
    ],
    category: "discovery",
  },
  {
    key: "advanced_analytics",
    name: "Advanced Analytics",
    tagline: "Full performance dashboard.",
    description:
      "Profile views, likes, match rate, best photos, peak times, and current queue depth. Spectrum has basic analytics; this is the full dashboard.",
    options: [
      { type: "monthly", amountCents: 299, unit: "/mo" },
      { type: "one_time", amountCents: 99, unit: "24h" },
    ],
    category: "profile",
  },
  {
    key: "replay_pack",
    name: "Extra REPLAY Pack",
    tagline: "10 credits to re-swipe profiles you passed.",
    description:
      "Each REPLAY credit lets you re-swipe one profile you previously passed (otherwise gone for 60 days). One-time only — no subscription.",
    options: [{ type: "one_time", amountCents: 199, unit: "10 credits" }],
    category: "discovery",
  },
  {
    key: "resonate_pack",
    name: "RESONATE Pack",
    tagline: "Communication-style matching. MindLynq only.",
    description:
      "Unlocks the communication-style matching layer: pace, format, response time, sensory considerations factor into your match ranking. Permanent after purchase.",
    options: [{ type: "one_time", amountCents: 299, unit: "permanent" }],
    appScope: "mindlynq",
    category: "discovery",
  },
  {
    key: "voice_wave_plus",
    name: "VOICE WAVE Plus",
    tagline: "More voice notes beyond your tier cap.",
    description:
      "Extends voice notes beyond Pulse's 10/month cap. Surge and above are already unlimited; the per-note option is the edge case there.",
    options: [
      { type: "monthly", amountCents: 399, unit: "/mo" },
      { type: "one_time", amountCents: 99, unit: "per note" },
    ],
    category: "communication",
  },
  {
    key: "community_verified",
    name: "Community Verified Badge",
    tagline: "Verified-member badge on your profile card.",
    description:
      "Shows the Community Verified badge after 30+ days active, zero flags, and a complete profile. Subscription alone doesn't grant it. Disappears if you lapse or get flagged.",
    options: [{ type: "monthly", amountCents: 299, unit: "/mo" }],
    category: "verification",
  },
  {
    key: "event_host_pass",
    name: "Event Host Pass",
    tagline: "Host a community meetup under Wavlynq.",
    description:
      "Verified members can host a single community event under the Wavlynq brand. All standard safety rules — barcode entry, ID check, pre-registration — still apply.",
    options: [{ type: "one_time", amountCents: 1499, unit: "per event" }],
    requiresEvent: true,
    category: "events",
  },
  {
    key: "profile_spotlight",
    name: "Profile Spotlight",
    tagline: "Pin your profile to the top of the community feed.",
    description:
      "Pins your profile to the top of the community social feed for 24 hours. Spectrum and Attuned already include one 2-week spotlight per cycle.",
    options: [{ type: "one_time", amountCents: 499, unit: "24h" }],
    category: "profile",
  },
];

export const fmtCents = (cents: number): string => {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
};

export const CATEGORY_LABELS: Record<AlacarteItem["category"], string> = {
  discovery: "Discovery",
  communication: "Communication",
  verification: "Verification",
  events: "Events",
  profile: "Profile",
};

// ---------------------------------------------------------------
// Checkout helper — calls the create-alacarte-checkout edge function.
// ---------------------------------------------------------------

export class AlacarteCheckoutError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "AlacarteCheckoutError";
  }
}

export interface StartAlacarteCheckoutOpts {
  featureKey: AlacarteFeatureKey;
  purchaseType: PurchaseType;
  /** Required for event_pass + event_host_pass. */
  eventId?: string;
  /** Required for resonate_pack (MindLynq-only). */
  app?: AppId;
  successPath?: string;
  cancelPath?: string;
}

export const startAlacarteCheckout = async (
  opts: StartAlacarteCheckoutOpts,
): Promise<{ url: string }> => {
  const origin = window.location.origin;
  const { data, error } = await supabase.functions.invoke<{
    url?: string;
    error?: string;
  }>("create-alacarte-checkout", {
    body: {
      feature_key: opts.featureKey,
      purchase_type: opts.purchaseType,
      success_url: `${origin}${opts.successPath ?? "/account?alacarte=success"}`,
      cancel_url: `${origin}${opts.cancelPath ?? "/a-la-carte?checkout=cancel"}`,
      ...(opts.eventId ? { event_id: opts.eventId } : {}),
      ...(opts.app ? { app: opts.app } : {}),
    },
  });

  if (data && "error" in data && data.error) {
    throw new AlacarteCheckoutError(String(data.error));
  }
  if (error) throw new AlacarteCheckoutError(error.message);
  if (!data?.url) {
    throw new AlacarteCheckoutError("Checkout session did not return a URL.");
  }
  return { url: data.url };
};
